//#region Imports and Global Definitions
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { StyleSheet, View, SafeAreaView, Platform, useWindowDimensions, FlatList } from 'react-native';
import { useTheme } from '@react-navigation/native';
import CustomLabel from '../../../components/CustomLabel/CustomLabel';
import CustomInput from '../../../components/CustomInput';
import CustomButton from '../../../components/CustomButton';
import { createCommonStyles } from '../../../styles/commonStyles';
import { ActivityIndicator, Card } from 'react-native-paper';
import { showMessage } from "react-native-flash-message";
import { useTableHelper } from '../../../hooks/useTableHelper';
import { useTranslation } from 'react-i18next';
import { useTraining } from '../../../hooks/useTraining';
import { generateUniqueID } from '../../../components/SurveyBuilderComponent/CreateSurvey/CreateSurveyFunctions';
//#endregion

const ListView = React.memo(({ item, index, styles, handleEditPress, handleDelete
 }) => {

    return (
        <SafeAreaView>
            <SafeAreaView style={styles.row}>
                <View style={[styles.item]}>
                    <CustomInput
                        value={item.code}
                        setValue={(e) => { handleEditPress(index, item, 'code', e) }}
                        maxLen={10}
                        type="MAININPUT"
                    />
                </View>
                <View style={[styles.item]}>
                    <CustomInput
                        value={item.description}
                        setValue={(e) => { handleEditPress(index, item, 'description', e) }}
                        maxLen={50}
                        type="MAININPUT"
                    />
                </View>
                <CustomButton
                    iconName='trash-outline'
                    onPress={() => { handleDelete(item, index, 'rowguid') }}
                    show='HOME_BELL'
                    type='HOME_BELL'
                    textTop='2'
                    sz={30} />
            </SafeAreaView>
        </SafeAreaView>
    );
});

const HRAdminLAndDScreen = (props) => {
    //#region Props
    const {  loadData, saveData, deleteData } = props;
    //#endregion

    //#region Const Definitions
    const ref = useRef(null);
    const { t, i18n } = useTranslation();
    const { width, height } = useWindowDimensions();
    const isPlatformWeb = (Platform.OS === 'web' && width > 992 ? false : true);
    const { colors } = useTheme();
    const common = createCommonStyles(colors);
    const styles = makeStyles(colors, width, height, isPlatformWeb);
    const [isLoading, setIsLoading] = useState(true);

    const [refreshAfterSave, setRefreshAfterSave] = useState(false);
    const { rowEdit, rowDelete, rowAdd, handleCancelChanges, hasDuplicates } = useTableHelper();

    const [LandDData, setLandDData] = useState([]);
    const [originalData, setOriginalData] = useState([]);
    const [changes, setChanges] = useState({});
    const [insertData, setInsertData] = useState([]);
    const [deletedItems, setDeletedItems] = useState([]);
    const [duplicates, setDuplicates] = useState([]);
    //#endregion

    useEffect(() => {
        setIsLoading(true);
        // Load Subjects
        loadData().then((res) => {
            setChanges({});
            setOriginalData([...res]);
            setLandDData(res);
            setRefreshAfterSave(false);
            setIsLoading(false);
        })
        .catch(() => {
            setRefreshAfterSave(false);
            setIsLoading(false);
        });
        setRefreshAfterSave(false);
        setIsLoading(false);
    },[refreshAfterSave])

    //#region Components
    const renderData = (item, index) => {
        return (
            <ListView item={item} index={index}
                styles={styles}
                handleEditPress={handleEditPress}
                handleDelete={handleDelete}
                insertData={insertData}
            />
        )
    }

    const renderLAndD = useCallback((item, index) => {
        return (
            <Card
                style={[common.cardstyle, { margin: 10 }]} >
                <Card.Content>
                    {renderData(item, index)}
                </Card.Content>
            </Card >
        )
    }, [LandDData]);
    //#endregion

    //#region Handlers
    const handleEditPress = useCallback((index, item, key, value) => {
        if (item[key] === value) return;
        rowEdit(index, item, key, value, changes, setChanges, LandDData, setLandDData)
    }, []);

    const handleDelete = (item, index, attribute) => {
        rowDelete(item, index, attribute, changes, setChanges, LandDData, setLandDData, setDeletedItems, ref)
    }

    const handleAddNew = () => {
        let temp = [...LandDData];
        if (LandDData.length === 0)
            temp = {
                code: "",
                description: "",
                rowguid: generateUniqueID()
            }
        rowAdd(temp, setLandDData, ['rowguid'], [], ref, t, [], [], undefined, undefined, insertData, setInsertData, id = 'rowguid',);
    }

    const removeFromChangesAfterSave = () => {
        setChanges([])
        setDeletedItems([])
    }

    const validateInput = () => {
        let ret = [true, t('common:validationSuccess')]
        LandDData.forEach((itr) => {
            if (itr.code === '')
                ret = [false, t('common:codeEmpty')];
            if (itr.description === '')
                ret = [false, t('common:descEmpty')]

        })
        return (ret);
    }

    const handleSave = async () => {
        let newPromises = [];
        let deleteSucc = true;
        let insertSucc = true;
        let updateSucc = true;
    
        if (Object.entries(changes).length == 0 && deletedItems.length == 0) {
            showMessage({
                message: t('common:noChanges'),
                type: "warning",
                duration: 3000,
            })
            return;
        }

        // Duplicate check on 'code' field
        let valid = true;
        const attributesToIgnore = ['description'];
        if (LandDData.length > 0) {
            Object.keys(LandDData[0]).forEach(key => {
                if (!attributesToIgnore.includes(key)) {
                    if (key === 'code' && hasDuplicates(LandDData, key, setDuplicates)) {
                        showMessage({
                            message: t('common:duplicates'),
                            type: "danger",
                            duration: 3000,
                        });
                        valid = false;
                    }
                }
            });
        }
        if (!valid) return;
    
        let valRet = validateInput();
        if (!valRet[0]) {
            showMessage({
                message: valRet[1],
                type: "danger",
                duration: 3000,
            })
            return;
        }
        setIsLoading(true);
        let dataForInsert = [];
        let dataForUpdate = [];

        Object.entries(LandDData).forEach(([key, obj]) => {
            if (insertData.includes(obj.rowguid)) {
                dataForInsert.push(obj);
            } else {
                dataForUpdate.push(obj);
            }
        });

        if (Object.keys(dataForUpdate).length > 0) {
            newPromises.push(saveData(dataForUpdate)
            .then((res) => {
                if (!res) updateSucc = false;
                removeFromChangesAfterSave()
            })
            .catch(() => {}));
        }

        if (Object.keys(dataForInsert).length > 0) {
            newPromises.push(saveData(dataForInsert)
            .then((res) => {
                if (!res) insertSucc = false;
                removeFromChangesAfterSave()
            })
            .catch(() => { }));
        }

        if (deletedItems.length > 0) {
            newPromises.push(deleteData(deletedItems).then((res) => {
                if (!res) deleteSucc = false;
                removeFromChangesAfterSave()
            })
            .catch(() => { }));
        }
    
        try {
            await Promise.all(newPromises).then(() => {
                setIsLoading(false);
                if (!insertSucc || !deleteSucc || !updateSucc) {
                    if (!deleteSucc) {
                        showMessage({
                            message: t('common:issueDeleteMessage'),
                            type: "danger",
                            duration: 3000,
                        })
                        let removeDeleteBranches = Object.fromEntries(Object.entries(changes).filter(([key, value]) =>
                            value.isDeleted !== true)
                        )
                        setChanges(removeDeleteBranches);
                    } else {
                        showMessage({
                            message: t('common:issueMessage'),
                            type: "danger",
                            duration: 3000,
                        })
                    }
                } else if (insertSucc && deleteSucc && updateSucc) {
                    showMessage({
                        message: t('common:allOperationsSuccMess'),
                        type: "success",
                        duration: 3000,
                    });
                    setDuplicates([]);
                    setRefreshAfterSave(true);
                }
            });
        } catch (error) {
            setRefreshAfterSave(true);
            setDuplicates([]);
        }
    }
    //#endregion

    //#region Loading
    if (isLoading)
        return (
            <View style={styles.loader}>
                <ActivityIndicator animating={true} color={colors.blue} />
            </View>
        )
    //#endregion

    return (
        <SafeAreaView style={styles.mainView}>
            <SafeAreaView style={styles.rowView}>
                <SafeAreaView style={[styles.subMain]}>
                    <SafeAreaView style={styles.innerView}>
                        <SafeAreaView style={[styles.buttons, styles.functionalityButtons]}>
                            <CustomButton
                                text={t('common:new')}
                                onPress={() => { handleAddNew() }}
                                type='BUTTON_ADD'
                                textTop='0'
                                sz={15}
                            />
                            <CustomButton
                                text={t('common:save')}
                                onPress={() => { handleSave() }}
                                type='BUTTON_SAVE'
                                textTop='0'
                                sz={15}
                                isGradient={true}
                            />
                            <CustomButton
                                text={t('common:cancel')}
                                onPress={() => { 
                                    handleCancelChanges(setLandDData, originalData, setDeletedItems, setChanges);
                                }}
                                type="BUTTON_CANCEL"
                                textTop='0'
                                sz={15}
                            />
                        </SafeAreaView>
                        <SafeAreaView style={styles.functionsView}>
                            <FlatList
                                style={styles.fullWidth}
                                data={LandDData}
                                keyExtractor={(item, index) => `${item.rowguid}`}
                                renderItem={({ item, index }) =>
                                    renderLAndD(item, index)
                                }
                                scrollEnabled={true}
                                refreshing={isLoading}
                                ref={ref}
                                ListHeaderComponent={
                                    <SafeAreaView>
                                        <View style={[styles.row, { marginRight: '3%', }]}>
                                            <CustomLabel
                                                value={t('common:code')}
                                                type='SUBTITLE' />
                                            <CustomLabel
                                                value={t('common:description')}
                                                type='SUBTITLE' />
                                        </View>
                                    </SafeAreaView>
                                }
                                ListEmptyComponent={
                                    isLoading ?
                                        <ActivityIndicator animating={true} color={colors.blue} />
                                        :
                                        <CustomLabel
                                            value={t("common:NoDataPreview")}
                                            type="NO_FUNCTIONS"
                                        />
                                }
                                initialNumToRender={4}
                            />
                        </SafeAreaView>
                    </SafeAreaView>
                </SafeAreaView>
            </SafeAreaView>
        </SafeAreaView>
    );
};

//#region Styles
const makeStyles = (colors, width) => StyleSheet.create({
    mainView: {
        flex: 1,
    },
    subMain: {
        borderRadius: 35,
        elevation: (Platform.OS === 'web' && width > 992 ? 3 : 0),
        shadowColor: colors.card_line,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.1,
        width: '100%'
    },
    rowView: {
        flex: 1,
        flexDirection: 'row',
        justifyContent: 'center'
    },
    row: {
        flex: 1,
        flexDirection: "row",
        alignItems: 'center',
    },
    item: {
        flex: 1,
    },
    error: {
        color: colors.validation_error,
        alignSelf: 'center',
        fontSize: 14,
    },
    innerView: {
        flex: 1,
    },
    functionsView: {
        flexDirection: 'row',
        height: '100%'
    },
    fullWidth: {
        height: '85%',
    },
    functionalityButtons: {
        justifyContent: 'flex-end'
    },
    buttons: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        width: '50%',
        alignSelf: 'flex-end',
    },
    loader: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '4%'
    },
});
//#endregion

export default HRAdminLAndDScreen;